import * as React from 'react'
import { useAlert, useEffectWithPolling, WidgetWrapper } from 'uxp/components'
import { IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate, validateUIProp } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { SelectableItem, selectUIProps } from '../../../common/SelectableItem'
import { ContainerProperty, extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { IProgressBarProperties, ProgressBarProperties, } from './ProgressBarProperties'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TextFieldProperty } from '../../../common/ui-props/TextFieldProperty'
import { SimpleColorProperty } from '../../../common/ui-props/ColorProperty'
import { FieldProperties, IFieldProperties } from './FieldProperties'
import { SimpleTextProperty } from '../../../common/ui-props/SimpleTextProperty'
import { IContextData, useInitialContextData } from '../../../../hooks/useContextData'
import './styles.scss'
import { hasValue, replaceWithData } from '../../../../util'
import { executeAndBind, processInitialSource } from '../../../common/data-source-picker/datasource'

const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {
    let uiProps = props?.uiProps || {};
    const alerts = useAlert()

    let [value1, setValue1] = React.useState(0);
    let [value2, setValue2] = React.useState(0);
    const [loading, setLoading] = React.useState(false)
    let [contextData, setContextData] = React.useState<IContextData>({})

    let defaultField1Props: IFieldProperties = {
        text: 'Current Value',
        color: '#253243',
        fieldName: '',
        useFieldAsTotal: false,
        datasource: null,
    };

    let defaultField2Props: IFieldProperties = {
        text: 'Total Value',
        color: '#253243',
        fieldName: '',
        useFieldAsTotal: true,
        datasource: null,
    };

    let defaultProgressbarProps: IProgressBarProperties = {
        backgroundColor: '#707070',
        barColorsForRanges: [{ from: 0, color: '#34c5b1' }],
        textColor: '#FFFFFF',
        icon: '',
    };

    const XPRProps: IUIPropertyDefinitionMap = {
        'container': ContainerProperty({
            background: {
                color: "",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            },

        }),
        'title': TextFieldProperty('Title', { text: '', backgroundColor: 'transparent', textColor: '#000000', fontSize: '1em' },
            {
                isBackgroundColorHidden: true,
                isFontSizeHidden: true,
                useCustomColorPicker: true
            }
        ),
        'subtitle': TextFieldProperty('Subtitle', { text: '', backgroundColor: 'transparent', textColor: '#424242', fontSize: '0.75em' },
            {
                isBackgroundColorHidden: true,
                isFontSizeHidden: true,
                useCustomColorPicker: true
            }
        ),
        'dataAreaBackgroundColor': SimpleColorProperty('Data Area Background Color', '#F3F7FD'),
        'progressbar': ProgressBarProperties(uiProps.progressbar || defaultProgressbarProps),
        'field1': FieldProperties('field-1', uiProps.field1 || defaultField1Props),
        'field2': FieldProperties('field-2', uiProps.field2 || defaultField2Props),
        'field2Total': SimpleTextProperty('Total (Hard-Coded)', ''),

        'channel': SimpleTextProperty('Channel', ''),
        'refreshInterval': SimpleTextProperty('Refresh Time', '5000'),
    }

    let progressbarProps: IProgressBarProperties = props.uiProps.progressbar || defaultProgressbarProps;
    let field1Props: IFieldProperties = props.uiProps.field1 || defaultField1Props;
    let field2Props: IFieldProperties = props.uiProps.field2 || defaultField2Props;

    let title = replaceWithData(uiProps?.title?.text?.trim() || 'Percentage In Progress Bar Widget', contextData);
    let titleColor = uiProps?.title?.textColor || '#000000';

    let dataAreaBackgroundColor = props.uiProps.dataAreaBackgroundColor || '#F3F7FD';

    let { styles: containerStyles } = extractContainerProperties(uiProps.container);

    let subtitle = replaceWithData(uiProps?.subtitle?.text?.trim() || 'Percentage', contextData);
    let subtitleColor = uiProps?.subtitle?.textColor || '#424242';

    let refreshInterval = Number(uiProps?.refreshInterval) || 2000;
    let channel = uiProps?.channel;

    const [initialSource, setIntitialSource] = React.useState(props.initialSource);
    const { isLoading, initialContextData } = useInitialContextData(props, [uiProps?.source])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    // validate data source 1
    const isDataSource1Valid = () => !hasValue(field1Props?.datasource) || !hasValue(field1Props?.datasource?.source) || !hasValue(field1Props?.datasource?.path)
    const validateDataSource1 = () => validateUIProp(isDataSource1Valid, 'datasource-1', 'field1-container', 'field-1-dataSource', 'Data source 1 is required', props?.designer)

    // validate data source 2
    const isDataSource2Valid = () => !hasValue(field2Props?.datasource) || !hasValue(field2Props?.datasource?.source) || !hasValue(field2Props?.datasource?.path)
    const validateDataSource2 = () => validateUIProp(isDataSource2Valid, 'datasource-2', 'field2-container', 'field-2-dataSource', 'Data source 2 is required', props?.designer)

    // validate field name 1
    const validateFieldName1 = () => validateUIProp(() => !hasValue(field1Props?.fieldName), 'field1Name', 'field1-container', 'field-1-name', "Please configure 'Field Name' for field 1", props?.designer)

    // validate field name 2
    const validateFieldName2 = () => validateUIProp(() => !hasValue(field2Props?.fieldName), 'field2Name', 'field2-container', 'field-2-name', "Please configure 'Field Name' for field 2", props?.designer)

    function validateBeforeSave() {
        if (!validateDataSource1()) return false
        if (!validateFieldName1()) return false
        if (!validateDataSource2()) return false
        if (!validateFieldName2()) return false

        return true
    }

    React.useEffect(() => {
        processInitialSource(props.designer, props.uxpContext, props.uiProps, initialSource, 'dictionary')
            .then(res => {
                if (res) {
                    console.log(res)
                    props.designer?.setUIProps({
                        ...uiProps,
                        datasources: res.datasources,
                        field1: { ...field1Props, datasource: res.source }
                    })
                }
            })
            .catch(e => {
                console.log('exception ', e);

            })

    }, [initialSource]);

    useEffectWithPolling(props.uxpContext, channel || "chart/1", refreshInterval || 2000, async () => {
        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
            setValue1(74);
            setValue2(553);
            return;
        }

        executeAndBind(props.uxpContext, field1Props.datasource, uiProps.datasources, {}, contextData)
            .then(res => {
                let value1 = Number(res[field1Props?.fieldName || 'count']);
                setValue1(value1);
            })


        executeAndBind(props.uxpContext, field2Props.datasource, uiProps.datasources, {}, contextData)
            .then(res => {
                let value2 = 0;
                if (Number(props.uiProps.field2Total)) {
                    value2 = Number(props.uiProps.field2Total);
                } else {
                    value2 = Number(res[field2Props?.fieldName || 'total']);
                }
                setValue2(value2);
            })
    }, [props.designer]);

    let total = 0;
    let pct = 0;

    if (field1Props?.useFieldAsTotal) {
        total = value1
        pct = (100 * value2 / total);
    } else if (field2Props?.useFieldAsTotal) {
        total = value2
        pct = (100 * value1 / total);
    } else {
        total = (value1 + value2)
        pct = (100 * value1 / total);
    }

    let width = Math.max(pct, 0);

    function getNearestProgressBarColor(pct: number) {
        let i = 0
        progressbarProps.barColorsForRanges?.map((v) => pct - v.from).reduce((previous, current, index) => {
            if (current < 0) return previous
            if (previous < current) return previous

            i = index
            return current
        })

        return progressbarProps?.barColorsForRanges[i]?.color
    }

    let progressBarColor = getNearestProgressBarColor(pct);

    console.log('field-1', uiProps?.field1)
    console.log('field-2', uiProps?.field2)

    const value1Label = replaceWithData(field1Props.text, contextData)
    const value2Label = replaceWithData(field2Props.text, contextData)

    return (
        <WidgetWrapper className='uxp-progress-bar-percentage-template'>
            <div className="bg" style={containerStyles}></div>
            <SelectableItem
                style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
                designer={props.designer}
                props={selectUIProps(uiProps, XPRProps, 'container', 'source', 'channel', 'refreshInterval')}
                elementId={'body-container'}
                contextData={contextData}
            >
                <div className='container'>
                    <div className='title-bar-wrapper'>
                        <SelectableItem
                            designer={props.designer}
                            props={selectUIProps(uiProps, XPRProps, 'container', 'title')}
                            contextData={contextData}
                        >
                            <div className="uxp-widget-title-bar uxp-drag-handler">
                                <div className="title-section">
                                    <span className="txt" style={{ color: titleColor }}>{title}</span>
                                </div>
                                <div className="action-section"></div>
                            </div>
                        </SelectableItem>
                    </div>
                    <div className='subtitle-wrapper'>
                        <SelectableItem
                            designer={props.designer}
                            props={selectUIProps(uiProps, XPRProps, 'subtitle')}
                            contextData={contextData}
                        >
                            <div className='subtitle' style={{ textTransform: 'uppercase', color: subtitleColor }}>{subtitle}</div>
                        </SelectableItem>
                    </div>

                    <div className="data-area-container-wrapper">
                        <div className="data-area-container">
                            <SelectableItem
                                designer={props.designer}
                                props={selectUIProps(uiProps, XPRProps, 'dataAreaBackgroundColor')}
                                contextData={contextData}
                            >
                                <div className='metric' style={{ backgroundColor: dataAreaBackgroundColor }}>
                                    <div className='labels'><div className='value1-section' style={{ color: field1Props?.color }}>

                                        <SelectableItem
                                            designer={props.designer}
                                            props={selectUIProps(uiProps, XPRProps, 'field1')}
                                            elementId={'field1-container'}
                                            contextData={contextData}
                                        >
                                            <div className='val'>{value1}</div>
                                            <div className='txt'>{value1Label}</div>

                                        </SelectableItem>
                                    </div>
                                        <div className='value2-section' style={{ color: field2Props?.color }}>

                                            <SelectableItem
                                                designer={props.designer}
                                                props={selectUIProps(uiProps, XPRProps, 'field2', 'field2Total')}
                                                elementId={'field2-container'}
                                                contextData={contextData}
                                            >
                                                <div className='val'>{value2}</div>
                                                <div className='txt'>{value2Label}</div>
                                            </SelectableItem>
                                        </div>
                                    </div>
                                    <div className='progress'>
                                        <SelectableItem
                                            designer={props.designer}
                                            props={selectUIProps(uiProps, XPRProps, 'progressbar')}
                                            contextData={contextData}
                                        >
                                            <div className='pbar' >
                                                <div className='fill' style={{ width: (width + '%'), backgroundColor: progressBarColor }} >
                                                    <FontAwesomeIcon className="icon" color={progressbarProps.textColor} icon={generateIconProp(progressbarProps?.icon)} />
                                                    <span style={{ color: progressbarProps.textColor }}>{(pct).toFixed(0) + '%'}</span>
                                                </div>
                                                <div className='remainder' style={{ backgroundColor: progressbarProps.backgroundColor }}>
                                                    <span style={{ color: progressbarProps.textColor }}>{(100 - pct).toFixed(0) + '%'}</span>
                                                </div>
                                            </div>
                                        </SelectableItem>
                                    </div>
                                </div>
                            </SelectableItem>
                        </div>
                    </div>
                </div>
            </SelectableItem>
        </WidgetWrapper>
    )
})

registerCardTemplate({
    id: 'progress-bar-percentage-widget',
    name: 'Progress Bar Percentage Widget',
    description: 'This widget can be used to represent a progress bar and labels using two values. Like current people count with total seat count .',
    template: XPR,
    icon: generateIconProp('tachometerAlt'),
    complexity: 'easy',
    expectedSchema: 'dictionary',
});

